import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import ShiftForm from '../../components/Forms/ShiftForm';
import ConfirmDialog from '../../components/ConfirmDialog';
import LogsAction from '../../components/LogsAction';
import shiftsApi from '../../api/masters/shiftsApi';
import { getSidebarState, saveSidebarState, saveScrollPosition, getScrollPosition } from '../../utils/stateManager';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import './MasterPage.css';

const DEFAULTS = { english_name: 150, hindi_name: 150, from_time: 80, to_time: 80, sequence: 80, status: 80, actions: 90 };

export default function Shifts() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, id: null, title: '', message: '' });
  const [message, setMessage] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const canViewMasters = hasPermission(PERMISSIONS.MASTERS_VIEW);
  const canManageMasters = hasPermission(PERMISSIONS.MASTERS_MANAGE);
  const canDeleteMasters = hasPermission(PERMISSIONS.MASTERS_DELETE);
  const navigate = useNavigate();
  const { colWidths, rHandle } = useResizableColumns('shifts-col-widths', DEFAULTS);

  useEffect(() => {
    setSidebarOpen(getSidebarState());
    if (canViewMasters) {
      fetchShifts();
    } else {
      setLoading(false);
    }
  }, [canViewMasters]);

  useEffect(() => {
    if (!showForm) {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const scrollPos = getScrollPosition('shifts-scroll');
        mainContent.scrollTop = scrollPos;

        const handleScroll = () => {
          saveScrollPosition('shifts-scroll', mainContent.scrollTop);
        };
        mainContent.addEventListener('scroll', handleScroll);
        return () => mainContent.removeEventListener('scroll', handleScroll);
      }
    }
  }, [showForm]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const response = await shiftsApi.getAll();
      const sorted = (response.data.data || []).sort((a, b) => {
        const seqA = a.sequence || 0;
        const seqB = b.sequence || 0;
        return seqA - seqB;
      });
      setShifts(sorted);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setMessage({ type: 'error', text: 'Failed to fetch shifts' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/login');
  };

  const handleMenuClick = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    saveSidebarState(newState);
  };

  const handleDragStart = (e, index) => {
    if (!canManageMasters) return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!canManageMasters) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    if (!canManageMasters) return;
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      return;
    }

    try {
      const newShifts = [...shifts];
      const draggedShift = newShifts[draggedItem];
      newShifts.splice(draggedItem, 1);
      newShifts.splice(dropIndex, 0, draggedShift);

      const updatedShifts = newShifts.map((shift, idx) => ({
        ...shift,
        sequence: idx + 1,
      }));

      setShifts(updatedShifts);
      setDraggedItem(null);

      await shiftsApi.updateSequence(updatedShifts);
      setMessage({ type: 'success', text: 'Sequence updated successfully' });
    } catch (error) {
      console.error('Error updating sequence:', error);
      setMessage({ type: 'error', text: 'Failed to update sequence' });
      fetchShifts();
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const confirmDelete = (id) => {
    setConfirm({
      open: true,
      id,
      title: 'Delete Shift',
      message: 'Are you sure you want to delete this shift? This action cannot be undone.',
    });
  };

  const handleDeleteConfirmed = async () => {
    const id = confirm.id;
    setConfirm({ ...confirm, open: false });
    try {
      await shiftsApi.delete(id);
      setShifts(shifts.filter((s) => s.id !== id));
      setMessage({ type: 'success', text: 'Shift deleted successfully' });
    } catch (error) {
      console.error('Error deleting shift:', error);
      setMessage({ type: 'error', text: 'Failed to delete shift' });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
    fetchShifts();
  };

  const handleEdit = (shift) => {
    setEditingId(shift.id);
    setShowForm(true);
  };

  return (
    <div className="dashboard-container">
      <Header onMenuClick={handleMenuClick} onLogout={handleLogout} />
      <div className="dashboard-content">
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content shifts-page">
          <div className="content-wrapper">
            {message && (
              <div className={`inline-message ${message.type === 'error' ? 'error' : 'success'}`}>
                {message.text}
                <button className="msg-close" onClick={() => setMessage(null)}>✕</button>
              </div>
            )}

            {!canViewMasters ? (
              <div className="inline-message error">You do not have permission to view masters.</div>
            ) : !showForm ? (
              <>
                <div className="list-header">
  <h1>Shifts Management</h1>
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {canManageMasters && (
                    <button
                      className="btn-primary small"
                      onClick={() => {
                        setEditingId(null);
                        setShowForm(true);
                      }}
                    >
                      + Add Shift
                    </button>
                  )}
    {canViewMasters && (
      <LogsAction
        category="shift"
        title="Shift Logs"
      />
    )}
  </div>
</div>

                <div className="table-container">
                  <table className="data-table draggable col-resizable" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th className="drag-handle"></th>
                        <th style={{ width: colWidths.english_name }}>English Name{rHandle('english_name')}</th>
                        <th style={{ width: colWidths.hindi_name }}>Hindi Name{rHandle('hindi_name')}</th>
                        <th style={{ width: colWidths.from_time }}>From{rHandle('from_time')}</th>
                        <th style={{ width: colWidths.to_time }}>To{rHandle('to_time')}</th>
                        <th style={{ width: colWidths.sequence }}>Sequence{rHandle('sequence')}</th>
                        <th style={{ width: colWidths.status }}>Status{rHandle('status')}</th>
                        <th style={{ width: 'auto', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="no-data">Loading...</td>
                        </tr>
                      ) : shifts.length > 0 ? (
                        shifts.map((shift, index) => (
                          <tr
                            key={shift.id}
                            draggable={canManageMasters}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`draggable-row ${draggedItem === index ? 'dragging' : ''}`}
                          >
                            <td className="drag-handle">
                              <span className="drag-icon">⋮⋮</span>
                            </td>
                            <td>{shift.shift_english}</td>
                            <td>{shift.shift_hindi}</td>
                            <td>{shift.shift_from || '-'}</td>
                            <td>{shift.shift_to || '-'}</td>
                            <td>{shift.sequence || '-'}</td>
                            <td>
                              <span className={`badge ${shift.is_active ? 'active' : 'inactive'}`}>
                                {shift.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {canManageMasters && (
                                <button
                                  className="btn-small btn-edit"
                                  onClick={() => handleEdit(shift)}
                                >
                                  Edit
                                </button>
                              )}
                              {canDeleteMasters && (
                                <button
                                  className="btn-small btn-delete"
                                  onClick={() => confirmDelete(shift.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="no-data">
                            No shifts found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <ShiftForm
                shiftId={editingId}
                onClose={handleFormClose}
                onSuccess={(msg) => setMessage(msg)}
              />
            )}

            <ConfirmDialog
              open={confirm.open}
              title={confirm.title}
              message={confirm.message}
              onCancel={() => setConfirm({ ...confirm, open: false })}
              onConfirm={handleDeleteConfirmed}
              confirmLabel="Delete"
              cancelLabel="Cancel"
            />
          </div>
        </main>
      </div>
    </div>
  );
}